import React, { useRef, useState, useLayoutEffect, useContext, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { AuthContext } from '../../context/AuthContext';
import { getManager } from '../../api/managerApi';
import './FileAttachment.css';

// 이미지 컨텍스트 생성 (확장자별 아이콘 이미지 불러오기)
const attachImages = require.context('../../image/attachImage', false, /\.gif$/);

function FileAttachment({ files = [], setFiles, mode = 'view', domainName, title = '첨부파일' }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]); // 선택된 파일 고유 식별자 관리
  const [baseDomain, setBaseDomain] = useState(domainName || '');
  const [domainError, setDomainError] = useState(false);
  const inputRef = useRef(null);
  const tbodyRef = useRef(null);

  const { user, token } = useContext(AuthContext);

  // M_ComCode로 회사정보를 조회하여 M_Domain 값으로 baseDomain 설정
  useEffect(() => {
    const loadDomain = async () => {
      try {
        if (!user?.M_ComCode) return;
        const manager = await getManager(user.M_ComCode, token);
        if (manager?.M_Domain) {
          setBaseDomain(manager.M_Domain);
          setDomainError(false);
        } else {
          setDomainError(true);
          console.warn('회사 M_Domain 값이 설정되지 않았습니다.');
          alert('회사의 도메인 정보가 설정되지 않아 파일 다운로드 기능을 사용할 수 없습니다.');
        }
      } catch (err) {
        console.error('회사 정보 조회 실패:', err);
        setDomainError(true);
        alert('회사 정보 조회 중 오류가 발생했습니다. 파일 다운로드 기능을 사용할 수 없습니다.');
      }
    };

    loadDomain();
  }, [user?.M_ComCode, token, domainName]);

  const onDrop = (acceptedFiles) => {
    const existingNames = (files || [])
      .filter(f => !f.isDeleted)
      .map(f => f.originalName || f.name);

    const duplicates = [];
    const uniqueFiles = acceptedFiles.filter(file => {
      if (existingNames.includes(file.name)) {
        duplicates.push(file.name);
        return false;
      }
      return true;
    });

    if (duplicates.length > 0) {
      alert(`다음 파일은 이미 첨부되어 있어 제외되었습니다:\n${duplicates.join(', ')}`);
    }

    if (uniqueFiles.length === 0) return;

    const newFiles = uniqueFiles.map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      isNew: true,
      isDeleted: false,
      // 신규 파일의 경우 임시 고유 ID 생성 (중복 방지)
      tempId: `new_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`
    }));
    setFiles(prev => [...prev, ...newFiles]);
  };

  // 파일의 고유 식별자를 반환하는 헬퍼 함수
  const getFileUid = (f, idx) => {
    return f.id || f.tempId || f.path || `${f.name}_${idx}`;
  };

  const handleRemoveFile = (f, idx) => {
    const uid = getFileUid(f, idx);
    setFiles(prev =>
      prev.map(item =>
        getFileUid(item, prev.indexOf(item)) === uid
          ? item.isNew
            ? null
            : { ...item, isDeleted: true }
          : item
      ).filter(Boolean)
    );
    setSelectedIds(prev => prev.filter(id => id !== uid));
  };

  const handleRemoveAll = () => {
    if (window.confirm('모든 첨부파일을 삭제하시겠습니까?')) {
      setFiles(prev =>
        prev.map(f => f.isNew ? null : { ...f, isDeleted: true }).filter(Boolean)
      );
      setSelectedIds([]);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true
  });

  const visibleFiles = (files || []).filter(f => f && !f.isDeleted);

  useLayoutEffect(() => {
    setCanExpand(visibleFiles.length > 4);
  }, [visibleFiles.length]);

  const getFileIconImg = (fileName) => {
    if (!fileName) return attachImages('./etc.gif');
    const ext = fileName.split('.').pop()?.toLowerCase();
    try {
      return attachImages(`./${ext}.gif`);
    } catch (e) {
      const imgExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'psd', 'ai', 'tiff'];
      if (imgExts.includes(ext)) {
        return attachImages('./etcimg.gif');
      }
      return attachImages('./etc.gif');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // --- 체크박스 및 다운로드 로직 ---

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(visibleFiles.map((f, idx) => getFileUid(f, idx)));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectFile = (e, uid) => {
    e.stopPropagation(); // 이벤트 전파 차단
    setSelectedIds(prev =>
      prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]
    );
  };

  const sanitizeFileName = (name) => {
    return name.replace(/[/\\:*?"<>|]/g, '_').replace(/_{2,}/g, '_');
  };

  const generateRandomKey = () => {
    return Math.random().toString(36).substring(2, 6).toUpperCase();
  };

  const downloadFile = async (url, fileName) => {
    const response = await fetch(url);
    const blob = await response.blob();
    saveAs(blob, fileName);
  };

  const handleDownloadSelected = async () => {
    if (!baseDomain) {
      alert('회사 도메인 정보가 설정되지 않아 파일을 다운로드할 수 없습니다.');
      return;
    }
    if (selectedIds.length === 0) {
      alert('다운로드할 파일을 선택해 주세요.');
      return;
    }

    const targets = visibleFiles.filter((f, idx) => selectedIds.includes(getFileUid(f, idx)));
    for (const f of targets) {
      const filePath = f.path ? f.path.replace(/\\/g, '/') : '';
      const url = baseDomain + filePath;
      await downloadFile(url, f.originalName || f.name);
    }
  };

  const handleDownloadZip = async () => {
    if (!baseDomain) {
      alert('회사 도메인 정보가 설정되지 않아 파일을 다운로드할 수 없습니다.');
      return;
    }
    if (visibleFiles.length === 0) return;

    const zip = new JSZip();
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const randomKey = generateRandomKey();
    const safeTitle = sanitizeFileName(title);
    const zipName = `${safeTitle}_(${dateStr}_${randomKey}).zip`;

    const targets = selectedIds.length > 0
      ? visibleFiles.filter((f, idx) => selectedIds.includes(getFileUid(f, idx)))
      : visibleFiles;

    if (targets.length === 0) return;

    alert(`${targets.length}개의 파일을 압축 중입니다. 잠시만 기다려 주세요...`);

    try {
      const promises = targets.map(async (f) => {
        const filePath = f.path ? f.path.replace(/\\/g, '/') : '';
        const url = baseDomain + filePath;
        const response = await fetch(url);
        const blob = await response.blob();
        zip.file(f.originalName || f.name, blob);
      });

      await Promise.all(promises);
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, zipName);
    } catch (error) {
      console.error('ZIP 생성 오류:', error);
      alert('파일 압축 중 오류가 발생했습니다.');
    }
  };

  if (visibleFiles.length === 0 && mode === 'view') {
    return null;
  }

  // 총 열 개수 계산
  let totalCols = 3; // 기본: 아이콘, 파일명, 크기
  if (mode === 'view') totalCols += 1; // 체크박스 추가
  if (mode === 'edit') totalCols += 1; // 관리 버튼 추가

  return (
    <div className={`file-attachment-premium mode-${mode}`}>
      <div
        {...(mode === 'edit' ? getRootProps() : {})}
        className={`attachment-data-grid ${isDragActive ? 'drag-active' : ''}`}
      >
        {mode === 'edit' && <input {...getInputProps()} ref={inputRef} />}

        <table className="premium-table">
          <thead>
            <tr className="table-action-row">
              <th colSpan={totalCols} className="header-action-th">
                <div className="table-header-content">
                  <div className="header-left">
                    {mode === 'edit' ? (
                      <>
                        <button
                          type="button"
                          className="btn-add-premium"
                          onClick={() => inputRef.current && inputRef.current.click()}
                        >
                          <i className="fas fa-plus"></i> 파일 추가
                        </button>
                        <span className="guide-text" style={{ fontWeight: 'bold' }}>
                          파일을 마우스로 끌어다 놓으셔도 됩니다.
                        </span>
                      </>
                    ) : (
                      <div className="view-mode-actions" style={{ marginLeft: 0 }}>
                        <button 
                          type="button" 
                          className="btn-action-view" 
                          onClick={handleDownloadSelected}
                          disabled={domainError}
                          title={domainError ? '도메인 정보가 없어 다운로드할 수 없습니다.' : '다운로드'}
                        >
                          <i className="fas fa-download"></i> 다운로드
                        </button>
                        <button 
                          type="button" 
                          className="btn-action-view highlight" 
                          onClick={handleDownloadZip}
                          disabled={domainError}
                          title={domainError ? '도메인 정보가 없어 다운로드할 수 없습니다.' : '모두저장'}
                        >
                          <i className="fas fa-file-archive"></i> 모두저장
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="header-right">
                    {mode === 'edit' && visibleFiles.length > 0 && (
                      <button
                        type="button"
                        className="btn-remove-all-top"
                        onClick={handleRemoveAll}
                        title="전체 삭제"
                      >
                        <i className="fas fa-trash-alt"></i> 전체삭제
                      </button>
                    )}
                    {(canExpand || isExpanded) && (
                      <button
                        type="button"
                        className="btn-toggle-expand-top"
                        onClick={() => setIsExpanded(!isExpanded)}
                        title={isExpanded ? "축소" : "전체 펼치기"}
                      >
                        <i className={`fas ${isExpanded ? 'fa-minus' : 'fa-plus'}`}></i>
                      </button>
                    )}
                  </div>
                </div>
              </th>
            </tr>
            <tr className="table-column-row">
              {mode === 'view' && (
                <th className="col-check">
                  <input
                    type="checkbox"
                    checked={visibleFiles.length > 0 && selectedIds.length === visibleFiles.length}
                    onChange={handleSelectAll}
                  />
                </th>
              )}
              <th className="col-icon"></th>
              <th className="col-name">파일명</th>
              <th className="col-size text-right">크기</th>
              {mode === 'edit' && <th className="col-action">삭제</th>}
            </tr>
          </thead>
          <tbody
            ref={tbodyRef}
            className={isExpanded ? 'is-full-height' : ''}
            style={{ overflowY: canExpand ? 'auto' : 'hidden' }}
          >
            {visibleFiles.length === 0 ? (
              <tr>
                <td colSpan={totalCols} className="empty-row">
                  첨부된 파일이 없습니다.
                </td>
              </tr>
            ) : (
              visibleFiles.map((f, idx) => {
                const displayName = f?.originalName || f?.name || 'Unknown File';
                const fileUid = getFileUid(f, idx);
                const filePath = f?.path ? f.path.replace(/\\/g, '/') : '';
                const url = baseDomain + filePath;
                const isSelected = selectedIds.includes(fileUid);

                return (
                  <tr key={fileUid} className={isSelected && mode === 'view' ? 'row-selected' : ''}>
                    {mode === 'view' && (
                      <td className="col-check text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleSelectFile(e, fileUid)}
                        />
                      </td>
                    )}
                    <td className="col-icon text-center"><img src={getFileIconImg(displayName)} alt="" className="file-icon-img" /></td>
                    <td className="col-name col-name-cell">
                      {mode === 'view' ? (
                        <a
                          href={url}
                          onClick={(e) => {
                            e.preventDefault();
                            downloadFile(url, displayName);
                          }}
                          className="premium-file-link"
                          title={displayName}
                        >
                          {displayName}
                        </a>
                      ) : (
                        <span className="premium-file-name" title={displayName}>{displayName}</span>
                      )}
                    </td>
                    <td className="col-size text-right size-cell">{formatSize(f?.size)}</td>
                    {mode === 'edit' && (
                      <td className="col-action text-center">
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(f, idx)}
                          className="premium-btn-remove"
                          title="삭제"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default FileAttachment;